import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { masterTablesKeys } from "./masterTables.keys";
import { useApi } from "../../api/ApiProvider";
import { useAuth } from "../Auth/auth.provider";
import type { TherapyType } from "../../api-client";

// All master tables (unwrapped array)
// Gated on an active session: the navigation panes that call this are mounted
// outside the authenticated routes, so without the guard this fires on the
// login screen and 401s.
export const useMasterTables = () => {
  const { masterlists } = useApi();
  const { authToken } = useAuth();
  return useQuery({
    queryKey: masterTablesKeys.masterTables.all(),
    queryFn: () => masterlists.getAllMasterTables() as unknown as Promise<any[]>,
    enabled: !!authToken,
    meta: { errorMessage: "Master-Tabellen konnten nicht geladen werden" },
    staleTime: 6 * 60 * 60 * 1000, // 6 hours,
  });
};

// ICD code search
export const useIcdCodes = (text: string | null, enabled: boolean = true) => {
  const { masterlists } = useApi();
  return useQuery({
    // queryKey: masterTablesKeys.icdCodes.detail(text!),
    queryKey:["icdCodes", text],
    queryFn: () => masterlists.getICDCCode(text!) as unknown as Promise<any>,
    enabled: text?true:false,
    meta: { errorMessage: "ICD-Code für normCode nicht gefunden" },
    staleTime: 6 * 60 * 60 * 1000, // 6 hours
  });
};

/**
 * Therapy display colours — reference data replacing the theme-file map.
 *
 * One flat Record keyed by catalog IDENTITY: TherapyMethod ids for scheduled
 * slots, PrescribedTherapy ids for unscheduled ones. Never keyed by rendered
 * text — see useTherapyColorMap. Server rows without a curated colour are
 * absent; callers fall back to grey.
 */
export const useTherapyColors = () => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: masterTablesKeys.therapyColors.all(),
    queryFn: () => masterlists.getTherapyColors(),
    meta: { errorMessage: "Therapiefarben konnten nicht geladen werden" },
    // Reference data: colours change by catalog curation, not by use.
    staleTime: 12 * 60 * 60 * 1000, // 12 hours
    gcTime: 12 * 60 * 60 * 1000,
  });
};

// Therapies list
export const useTherapies = (therapyType?: TherapyType) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: therapyType
      ? [...masterTablesKeys.therapies.all(), { therapyType }]
      : masterTablesKeys.therapies.all(),
    queryFn: () => masterlists.getTherapies(therapyType),
    meta: { errorMessage: "Therapien konnten nicht geladen werden" },
    staleTime: 6 * 60 * 60 * 1000, // 6 hours
  });
};

// Locations list
export const useLocations = () => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: masterTablesKeys.locations.all(),
    queryFn: () => masterlists.getLocations(),
    meta: { errorMessage: "Standorte konnten nicht geladen werden" },
    staleTime: 6 * 60 * 60 * 1000, // 6 hours
  });
};

// Businesses list
export const useBusinesses = () => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: masterTablesKeys.businesses.all(),
    queryFn: () => masterlists.getBusinesses(),
    meta: { errorMessage: "Geschäftseinheiten konnten nicht geladen werden" },
    staleTime: 6 * 60 * 60 * 1000, // 6 hours
  });
};

// Locations for Business
export const useLocationsForBusiness = (businessId: string) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: masterTablesKeys.locations.forBusiness(businessId),
    queryFn: () => masterlists.getLocationsForBusiness(businessId),
    meta: { errorMessage: "Standorte für Geschäftseinheit konnten nicht geladen werden" },
    staleTime: 6 * 60 * 60 * 1000, // 6 hours
    enabled: !!businessId,
  });
};
/**
 * Prescribed TERMS as Rx-entry options — what a prescription may name.
 *
 * The Rx forms must offer terms, not billable line items: the line item is
 * payer- and credential-bound and is resolved per slot at scheduling (MD-232).
 * Each option carries its aliases so typed text ("Bobath", "Fango", BG
 * position "8101") matches the term it stands for, mirroring the backend's own
 * alias resolution on save.
 *
 * `therapyType` filters by discipline; terms valid in both (Hausbesuch,
 * Bericht) are always included by the server.
 */
export const usePrescribedTherapyOptions = (therapyType?: TherapyType) => {
  const { masterlists } = useApi();
  return useQuery({
    queryKey: therapyType
      ? [...masterTablesKeys.prescribedTherapyOptions.all(), { therapyType }]
      : masterTablesKeys.prescribedTherapyOptions.all(),
    queryFn: () => masterlists.getPrescribedTherapyOptions(therapyType as any),
    meta: { errorMessage: "Heilmittel-Katalog konnte nicht geladen werden" },
    // Catalog reference data: changes by curation, not by use.
    staleTime: 12 * 60 * 60 * 1000,
    gcTime: 12 * 60 * 60 * 1000,
  });
};

/**
 * `(prescribedTherapyId, tarifGroupId) → provisional minutes` from the same
 * 12h-cached catalog feed: each term's default line item per tarif group (the
 * resolution winner for a fully-credentialed therapist).
 *
 * This is what the calendar assumes while dragging a term-only stub — good
 * enough to draw and lay out the slot, deliberately NOT authoritative: the
 * backend re-resolves the line item at scheduling, clamps the window to its
 * catalog duration and returns the corrected survivor DTO, which replaces the
 * optimistic cache entry.
 *
 * An unknown term or tarif group answers `undefined`, so the caller applies its
 * own fallback. It must NOT answer with some other tarif group's duration:
 * durations are NOT method-level facts across payers — PFB is 60 minutes under
 * GKV, Privat and ErgoBlanko but 75 under DGUV, and HLT is 30 against 45. The
 * map is built ordered by tarif group id, so "any entry" meant "BG", and a
 * Blanko Dauertermin was laid out at the DGUV Regelleistungszeit. That is
 * normally invisible, because scheduling clamps the window to the resolved
 * item's catalog duration — but an interval-priced position (§ 125a Ergo) is
 * exempt from that clamp, its length being the billed quantity, so the wrong
 * guess was persisted and billed.
 */
export const useTermDefaultDurationLookup = (): ((
  prescribedTherapyId?: string | null,
  tarifGroupId?: string | null
) => number | undefined) => {
  const { data: options } = usePrescribedTherapyOptions();
  return useMemo(() => {
    const byTerm = new Map<string, Record<string, number>>();
    for (const option of options ?? []) {
      byTerm.set(option.id, (option as any).defaultDurations ?? {});
    }
    return (prescribedTherapyId?: string | null, tarifGroupId?: string | null) => {
      if (!prescribedTherapyId) return undefined;
      const durations = byTerm.get(prescribedTherapyId);
      if (!durations) return undefined;
      if (!tarifGroupId) return undefined;
      return durations[tarifGroupId] ?? undefined;
    };
  }, [options]);
};

/**
 * `(prescribedTherapyId, tarifGroupId) → isPassive` of the SAME default line
 * item `useTermDefaultDurationLookup` reads the duration from (MD-550).
 *
 * This is what lets a term-only slot — an unscheduled Dauertermin, a shelf
 * stub — take part in the MD-314 contract: the drag ghost leaves a leading
 * Warmpackung out of the time it claims at the drop point, and the layout
 * starts the main therapy exactly there. The concrete shelf reads
 * `therapy.isPassive` off the slot's resolved line item for this; a template
 * has none, so it reads the catalog default instead. One winner per (term,
 * tarif group) supplies both duration and passivity, so the two cannot
 * disagree.
 *
 * A planning hint, exactly like the duration: `WAERME` answers with its
 * default (HL, passive) although a therapist without that credential ends up
 * delivering HR — the server re-resolves the line item at scheduling. Unknown
 * term or tarif group answers `undefined`, which `layoutSlots` treats as
 * active (no shift on a guess).
 */
export const useTermDefaultPassivityLookup = (): ((
  prescribedTherapyId?: string | null,
  tarifGroupId?: string | null
) => boolean | undefined) => {
  const { data: options } = usePrescribedTherapyOptions();
  return useMemo(() => {
    const byTerm = new Map<string, Record<string, boolean>>();
    for (const option of options ?? []) {
      byTerm.set(option.id, (option as any).defaultPassivity ?? {});
    }
    return (prescribedTherapyId?: string | null, tarifGroupId?: string | null) => {
      if (!prescribedTherapyId || !tarifGroupId) return undefined;
      const passivity = byTerm.get(prescribedTherapyId);
      if (!passivity) return undefined;
      const value = passivity[tarifGroupId];
      return typeof value === "boolean" ? value : undefined;
    };
  }, [options]);
};
